import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, Upload, X, Check } from "lucide-react";
import { api, formatApiError, absUrl } from "@/lib/api";
import { CATEGORIES } from "@/lib/site";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const EMPTY = {
    title: "",
    client: "",
    category: "",
    year: "",
    role: "Cinematography",
    description: "",
    video_url: "",
    thumbnail_url: "",
    featured: false,
    sort_order: 0,
};

function UploadField({ label, accept, value, onDone, testid }) {
    const [busy, setBusy] = useState(false);
    const [pct, setPct] = useState(0);

    const upload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setBusy(true);
        setPct(0);
        const fd = new FormData();
        fd.append("file", file);
        try {
            const { data } = await api.post("/admin/upload", fd, {
                onUploadProgress: (ev) => setPct(ev.total ? Math.round((ev.loaded / ev.total) * 100) : 0),
            });
            onDone(data.url);
            toast.success("Upload complete");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="md:col-span-2">
            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-inkdim">{label}</label>
            <div className="mt-2 flex items-center flex-wrap gap-3">
                <label
                    data-testid={testid}
                    className="cursor-pointer inline-flex items-center gap-2 border border-ink/30 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] hover:bg-ink hover:text-paper transition-colors"
                >
                    <Upload size={14} /> {busy ? `Uploading ${pct}%` : "Choose file"}
                    <input type="file" accept={accept} className="hidden" onChange={upload} />
                </label>
                {value && !busy && (
                    <span className="text-xs text-inkdim inline-flex items-center gap-1">
                        <Check size={12} /> Uploaded
                        <button type="button" onClick={() => onDone("")} className="ml-1 text-inkdim hover:text-ink">
                            <X size={13} />
                        </button>
                    </span>
                )}
            </div>
            {busy && (
                <div className="mt-2 h-0.5 bg-line">
                    <div className="h-full bg-ink transition-all" style={{ width: `${pct}%` }} />
                </div>
            )}
        </div>
    );
}

export default function ProjectsPanel() {
    const [projects, setProjects] = useState(null);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(null);

    const load = () =>
        api.get("/projects")
            .then((r) => setProjects(r.data))
            .catch(() => setProjects([]));
    useEffect(() => {
        load();
    }, []);

    const openNew = () => {
        setEditing(null);
        setForm(EMPTY);
        setOpen(true);
    };
    const openEdit = (p) => {
        setEditing(p);
        setForm({ ...EMPTY, ...p });
        setOpen(true);
    };
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const save = async () => {
        if (!form.title.trim()) {
            toast.error("Title is required");
            return;
        }
        setSaving(true);
        try {
            if (editing) await api.put(`/admin/projects/${editing.id}`, form);
            else await api.post("/admin/projects", form);
            toast.success(editing ? "Project updated" : "Project added");
            setOpen(false);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const del = async () => {
        try {
            await api.delete(`/admin/projects/${deleting.id}`);
            toast.success("Project removed");
            setDeleting(null);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    return (
        <div>
            <div className="flex items-center justify-between flex-wrap gap-4">
                <h2 className="font-display font-extrabold uppercase text-2xl tracking-tight" data-testid="admin-projects-count">
                    Projects {projects ? `(${projects.length})` : ""}
                </h2>
                <Button
                    data-testid="admin-new-project"
                    onClick={openNew}
                    className="rounded-none bg-ink text-paper hover:bg-inkdim uppercase text-xs font-bold tracking-[0.18em]"
                >
                    <Plus size={14} /> New project
                </Button>
            </div>

            {!projects ? (
                <p className="py-16 flex justify-center">
                    <Loader2 className="animate-spin" />
                </p>
            ) : (
                <div className="mt-8 overflow-x-auto border border-line">
                    <table className="w-full text-sm" data-testid="admin-projects-table">
                        <thead>
                            <tr className="border-b border-line text-left text-[10px] uppercase tracking-[0.18em] text-inkdim">
                                <th className="p-3 font-bold">#</th>
                                <th className="p-3 font-bold">Title</th>
                                <th className="p-3 font-bold">Category</th>
                                <th className="p-3 font-bold">Year</th>
                                <th className="p-3 font-bold">Video</th>
                                <th className="p-3 font-bold">Featured</th>
                                <th className="p-3 font-bold">Order</th>
                                <th className="p-3 font-bold text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {projects.map((p, i) => (
                                <tr key={p.id} className="border-b border-line last:border-0 hover:bg-surface/60" data-testid={`admin-project-row-${p.slug}`}>
                                    <td className="p-3 text-inkdim">{i + 1}</td>
                                    <td className="p-3">
                                        <div className="flex items-center gap-3">
                                            {p.thumbnail_url && (
                                                <img src={absUrl(p.thumbnail_url)} alt="" className="h-10 w-16 object-cover" />
                                            )}
                                            <div>
                                                <p className="font-bold">{p.title}</p>
                                                <p className="text-xs text-inkdim">{p.client}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="p-3 text-inkdim">{p.category}</td>
                                    <td className="p-3">{p.year}</td>
                                    <td className="p-3">
                                        {p.video_url ? (
                                            <span className="text-green-700 font-bold">Yes</span>
                                        ) : (
                                            <span className="text-inkdim">No</span>
                                        )}
                                    </td>
                                    <td className="p-3">{p.featured ? "Featured" : "—"}</td>
                                    <td className="p-3">{p.sort_order}</td>
                                    <td className="p-3 text-right whitespace-nowrap">
                                        <button
                                            data-testid={`admin-edit-${p.slug}`}
                                            onClick={() => openEdit(p)}
                                            className="p-2 hover:opacity-60"
                                            aria-label={`Edit ${p.title}`}
                                        >
                                            <Pencil size={15} />
                                        </button>
                                        <button
                                            data-testid={`admin-delete-${p.slug}`}
                                            onClick={() => setDeleting(p)}
                                            className="p-2 hover:opacity-60 text-red-700"
                                            aria-label={`Delete ${p.title}`}
                                        >
                                            <Trash2 size={15} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent
                    className="rounded-none bg-paper border-ink/15 max-w-2xl max-h-[90vh] overflow-y-auto"
                    data-testid="admin-project-dialog"
                >
                    <DialogHeader>
                        <DialogTitle className="font-display font-extrabold uppercase tracking-tight text-2xl">
                            {editing ? "Edit project" : "New project"}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-2">
                        <div className="md:col-span-2">
                            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-inkdim">Title *</label>
                            <Input
                                data-testid="admin-field-title"
                                value={form.title}
                                onChange={(e) => set("title", e.target.value)}
                                className="mt-1.5 rounded-none border-ink/30 bg-transparent"
                                placeholder="Project title"
                            />
                        </div>
                        <div>
                            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-inkdim">Client</label>
                            <Input
                                data-testid="admin-field-client"
                                value={form.client}
                                onChange={(e) => set("client", e.target.value)}
                                className="mt-1.5 rounded-none border-ink/30 bg-transparent"
                                placeholder="Client / production house"
                            />
                        </div>
                        <div>
                            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-inkdim">Year</label>
                            <Input
                                data-testid="admin-field-year"
                                value={form.year}
                                onChange={(e) => set("year", e.target.value)}
                                className="mt-1.5 rounded-none border-ink/30 bg-transparent"
                                placeholder="2025"
                            />
                        </div>
                        <div>
                            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-inkdim">Category</label>
                            <Select value={form.category} onValueChange={(v) => set("category", v)}>
                                <SelectTrigger data-testid="admin-field-category" className="mt-1.5 rounded-none border-ink/30 bg-transparent w-full">
                                    <SelectValue placeholder="Select category" />
                                </SelectTrigger>
                                <SelectContent className="rounded-none max-h-72">
                                    {CATEGORIES.map((c) => (
                                        <SelectItem key={c} value={c}>
                                            {c}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-inkdim">Role</label>
                            <Input
                                data-testid="admin-field-role"
                                value={form.role}
                                onChange={(e) => set("role", e.target.value)}
                                className="mt-1.5 rounded-none border-ink/30 bg-transparent"
                                placeholder="Cinematography & Direction"
                            />
                        </div>
                        <div className="md:col-span-2">
                            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-inkdim">Description</label>
                            <Textarea
                                data-testid="admin-field-description"
                                value={form.description}
                                onChange={(e) => set("description", e.target.value)}
                                className="mt-1.5 rounded-none border-ink/30 bg-transparent min-h-[90px]"
                                placeholder="One or two lines about the film…"
                            />
                        </div>
                        <div className="md:col-span-2 flex items-center justify-between border border-ink/15 p-4">
                            <span className="text-xs font-bold uppercase tracking-[0.18em]">Featured project</span>
                            <Switch
                                data-testid="admin-field-featured"
                                checked={form.featured}
                                onCheckedChange={(v) => set("featured", v)}
                            />
                        </div>
                        <div>
                            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-inkdim">Sort order</label>
                            <Input
                                data-testid="admin-field-order"
                                type="number"
                                value={form.sort_order}
                                onChange={(e) => set("sort_order", parseInt(e.target.value || "0", 10))}
                                className="mt-1.5 rounded-none border-ink/30 bg-transparent"
                            />
                        </div>
                        <UploadField
                            label="Video file (mp4 / mov)"
                            accept="video/*"
                            value={form.video_url}
                            onDone={(u) => set("video_url", u)}
                            testid="admin-upload-video"
                        />
                        <UploadField
                            label="Thumbnail / poster image"
                            accept="image/*"
                            value={form.thumbnail_url}
                            onDone={(u) => set("thumbnail_url", u)}
                            testid="admin-upload-thumbnail"
                        />
                    </div>
                    <div className="flex justify-end gap-3 mt-6">
                        <Button
                            variant="outline"
                            onClick={() => setOpen(false)}
                            className="rounded-none border-ink/30 uppercase text-xs font-bold tracking-[0.18em]"
                        >
                            Cancel
                        </Button>
                        <Button
                            data-testid="admin-save-project"
                            onClick={save}
                            disabled={saving}
                            className="rounded-none bg-ink text-paper hover:bg-inkdim uppercase text-xs font-bold tracking-[0.18em]"
                        >
                            {saving ? <Loader2 className="animate-spin" size={14} /> : editing ? "Save changes" : "Add project"}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
                <AlertDialogContent className="rounded-none bg-paper border-ink/15">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="font-display uppercase">Delete "{deleting?.title}"?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This removes the project from the site. This cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-none border-ink/30">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="admin-confirm-delete"
                            onClick={del}
                            className="rounded-none bg-red-700 text-white hover:bg-red-800"
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
